import React, { useEffect, useMemo, useState } from "react";
import type { User } from "firebase/auth";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Row,
  Spinner,
  Tab,
  Tabs,
} from "react-bootstrap";
import { getGlobalLegal, upsertGlobalLegal } from "../../api/legal";
import type { GlobalLegalDocument } from "../../models/legal";

const todayIso = (): string => new Date().toISOString().slice(0, 10);

const emptyDoc = (): GlobalLegalDocument => ({
  terms: { es: "", en: "", version: "" },
  privacyPolicy: { es: "", en: "", version: "" },
  updatedAt: null,
});

const MarkdownField = ({
  label,
  value,
  onChange,
  disabled,
  rows = 12,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  disabled: boolean;
  rows?: number;
}) => (
  <div>
    <div className="d-flex justify-content-between align-items-center mb-2">
      <div className="fw-semibold">{label}</div>
      <div className="text-muted small">Markdown (GFM)</div>
    </div>

    <Tabs defaultActiveKey="edit" className="mb-2">
      <Tab eventKey="edit" title="Edit">
        <Form.Group controlId={`${label}-editor`} className="mb-2">
          <Form.Control
            as="textarea"
            rows={rows}
            value={value}
            onChange={(event) => onChange(event.target.value)}
            disabled={disabled}
            spellCheck={false}
            style={{
              fontFamily:
                "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
            }}
          />
        </Form.Group>
      </Tab>
      <Tab eventKey="preview" title="Preview">
        <div
          className="border rounded bg-body-tertiary p-3"
          style={{ maxHeight: 420, overflow: "auto" }}
        >
          <div className="text-body">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {value?.trim() ? value : "_Nothing to preview yet._"}
            </ReactMarkdown>
          </div>
        </div>
      </Tab>
    </Tabs>
  </div>
);

const LegalEditorPage: React.FC<{ user: User }> = ({ user }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [doc, setDoc] = useState<GlobalLegalDocument>(emptyDoc());

  const hasVersions = useMemo(
    () => !!doc.terms.version && !!doc.privacyPolicy.version,
    [doc.terms.version, doc.privacyPolicy.version]
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setSuccess(null);

    getGlobalLegal()
      .then((value) => {
        if (cancelled) return;
        setDoc(value ?? emptyDoc());
      })
      .catch((err) => {
        if (cancelled) return;
        setError(
          err instanceof Error ? err.message : "Unable to load legal doc."
        );
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const handlePublish = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await upsertGlobalLegal({
        terms: {
          es: doc.terms.es,
          en: doc.terms.en,
          version: doc.terms.version || todayIso(),
        },
        privacyPolicy: {
          es: doc.privacyPolicy.es,
          en: doc.privacyPolicy.en,
          version: doc.privacyPolicy.version || todayIso(),
        },
        updatedBy: user.uid,
      });
      setSuccess("Published legal updates successfully.");
      const refreshed = await getGlobalLegal();
      if (refreshed) setDoc(refreshed);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Publish failed.");
    } finally {
      setSaving(false);
    }
  };

  const setTerms = (patch: Partial<GlobalLegalDocument["terms"]>) =>
    setDoc((prev) => ({ ...prev, terms: { ...prev.terms, ...patch } }));
  const setPrivacy = (patch: Partial<GlobalLegalDocument["privacyPolicy"]>) =>
    setDoc((prev) => ({
      ...prev,
      privacyPolicy: { ...prev.privacyPolicy, ...patch },
    }));

  if (loading) {
    return (
      <div className="d-flex align-items-center gap-2 text-muted">
        <Spinner animation="border" size="sm" />
        <span>Loading legal content…</span>
      </div>
    );
  }

  return (
    <>
      <Card className="shadow-sm mb-4">
        <Card.Body>
          <Card.Title className="mb-2">Legal content (global)</Card.Title>
          <Card.Text className="text-muted mb-0">
            Publishing here updates the canonical Terms & Privacy versions used
            across MayorX. When versions change, apps must prompt users to
            re-accept.
          </Card.Text>
        </Card.Body>
      </Card>

      {error ? (
        <Alert variant="danger" className="shadow-sm">
          {error}
        </Alert>
      ) : null}

      {success ? (
        <Alert variant="success" className="shadow-sm">
          {success}
        </Alert>
      ) : null}

      <Row xs={1} lg={2} className="g-4">
        <Col>
          <Card className="shadow-sm h-100">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
                <div>
                  <Card.Title className="mb-0">Terms & Conditions</Card.Title>
                  <div className="text-muted small">
                    Current version:{" "}
                    <strong>{doc.terms.version || "Not set"}</strong>
                  </div>
                </div>
                <Form.Group controlId="termsVersion" style={{ minWidth: 160 }}>
                  <Form.Label className="small text-muted mb-1">
                    Version (YYYY-MM-DD)
                  </Form.Label>
                  <Form.Control
                    size="sm"
                    value={doc.terms.version}
                    placeholder={todayIso()}
                    onChange={(event) =>
                      setTerms({ version: event.target.value })
                    }
                    disabled={saving}
                  />
                </Form.Group>
              </div>

              <div className="mb-4">
                <MarkdownField
                  label="Terms (ES)"
                  value={doc.terms.es}
                  onChange={(next) => setTerms({ es: next })}
                  disabled={saving}
                />
              </div>

              <MarkdownField
                label="Terms (EN)"
                value={doc.terms.en}
                onChange={(next) => setTerms({ en: next })}
                disabled={saving}
              />
            </Card.Body>
          </Card>
        </Col>

        <Col>
          <Card className="shadow-sm h-100">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
                <div>
                  <Card.Title className="mb-0">Privacy Policy</Card.Title>
                  <div className="text-muted small">
                    Current version:{" "}
                    <strong>{doc.privacyPolicy.version || "Not set"}</strong>
                  </div>
                </div>
                <Form.Group
                  controlId="privacyVersion"
                  style={{ minWidth: 160 }}
                >
                  <Form.Label className="small text-muted mb-1">
                    Version (YYYY-MM-DD)
                  </Form.Label>
                  <Form.Control
                    size="sm"
                    value={doc.privacyPolicy.version}
                    placeholder={todayIso()}
                    onChange={(event) =>
                      setPrivacy({ version: event.target.value })
                    }
                    disabled={saving}
                  />
                </Form.Group>
              </div>

              <div className="mb-4">
                <MarkdownField
                  label="Privacy (ES)"
                  value={doc.privacyPolicy.es}
                  onChange={(next) => setPrivacy({ es: next })}
                  disabled={saving}
                />
              </div>

              <MarkdownField
                label="Privacy (EN)"
                value={doc.privacyPolicy.en}
                onChange={(next) => setPrivacy({ en: next })}
                disabled={saving}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <div className="d-flex justify-content-end gap-2 mt-4">
        <Button
          variant="primary"
          onClick={handlePublish}
          disabled={saving || !hasVersions}
        >
          {saving ? "Publishing…" : "Publish update"}
        </Button>
      </div>
    </>
  );
};

export default LegalEditorPage;
